import { styled } from "@mui/system";
import { ObjectId } from "bson";
import React from "react";

/* Redux Imports */
import { BusinessProfileInterface, ServiceInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";
import { DbUserPage } from "../../../../../redux-magic/sub-interfaces/user-interface";
import {
	setCreateNewServiceUploadImagesThunk,
	setServiceImagesUploadCompletedThunk,
	setServiceIsImagesUploadingThunk,
} from "../../../../../redux-magic/thunks";

/* Component Imports */

import {
	Alert,
	Chip,
	CircularProgress,
	FormControl,
	IconButton,
	RadioGroup,
	Snackbar,
	SvgIcon,
	Typography,
	useTheme,
} from "@mui/material";
import { useRouter } from "next/router";
import uploadFiles from "../../../../../lib/uploadFiles";
import validateFiles from "../../../../../lib/validateFiles";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";

/* Icon Imports */
import { DeleteOutline } from "@mui/icons-material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RequestImagesIcon from "../../../../../public/images/icons/singleproperty/request_image.svg";
const displayImage: string = "images/uploads/displayImage.jpg";

/* Interfaces */
interface LocalServiceInterface extends Omit<ServiceInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

interface FileAndMore {
	file: File;
	mime_type: string;
}

/* Styled Components */

const CustomDialog = styled("div")(({ theme }) => ({
	width: "100%",
}));

const CustomDialogContent = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "0rem",
	padding: "1rem 0rem 0rem 0rem",
	background: theme.palette.background.paper,
}));

const InputContent = styled("input")(({ theme }) => ({
	display: "none",
}));

const UploadContent = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	border: "1px dashed var(--Other-New-Outline-Border, rgba(0, 0, 0, 0.23))",
	borderRadius: "1rem",
	cursor: "pointer",
	borderColor: "#0000003B",
	// height: "10rem",
	padding: "2rem",
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "47%",
	height: "10.5rem",
	borderRadius: "16px",
	borderColor: theme.palette.primary.main,
}));

const SecondaryUploadContent = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "15rem",
	height: "5.238rem",
	border: "2px dashed #EE4B2B",
	borderRadius: "16px",
	cursor: "pointer",
	borderColor: theme.palette.primary.main,
}));

const ImageContainer = styled("div")(({ theme }) => ({
	position: "relative",
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
}));

const PreviewImage = styled("img")(({ theme }) => ({
	width: "100%",
	height: "10.238rem",
	borderRadius: "16px ",
	// border: "1px dashed rgba(0, 0, 0, 0.40)",
	// borderColor: theme.palette.primary.main,
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	[theme.breakpoints.down("md")]: {
		bottom: "5rem",
		borderRadius: "0.5rem",
	},
}));

const FormControlContainer = styled(FormControl)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	padding: "var(--Spacing-spacing-sm, 0.5rem)",
	gap: "var(--Spacing-spacing-sm, 0.5rem)",
	alignSelf: "stretch",
	width: "100%",
	height: "3rem",
	position: "absolute",
	bottom: "0.3rem",
}));

const ProgressContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	position: "absolute",
	width: "100%",
	height: "100%",
	zIndex: 0,
}));

const ImagePreviewContainer = styled(RadioGroup)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	maxHeight: "30rem",
	gap: "1rem",
	flexWrap: "wrap",
}));

const DividerContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "center",
	gap: "8px",
	width: "100%",
	margin: "0rem",
}));

const UploadImages = ({
	newServiceData,
	session,
	dispatch,
	businessProfiles,
	isBusinessProfile,
	isNewBusinessProfile,
}: {
	newServiceData: StateInterface["new_service_data"];
	session: StateInterface["redux_session"];
	dispatch: Function;
	businessProfiles: DbUserPage;
	isBusinessProfile: boolean;
	isNewBusinessProfile: boolean;
}) => {
	const theme = useTheme();

	const input = React.useRef<HTMLInputElement>(null);

	const [selectedFiles, setSelectedFiles] = React.useState<Array<FileAndMore>>([]);

	const [stage, setStage] = React.useState<Array<string>>(
		newServiceData?.images.map((image) => image.stage) || ["NOT_STARTED"],
	);

	const allFilesFetched = stage.every((status) => status === "FETCHED_FILE_INFORMATION");

	const [fileId, setFileId] = React.useState<Array<ObjectId | string | null>>(
		newServiceData?.images.map((image) => image.file_id) || [null],
	);

	const [mimeType, setMimeType] = React.useState<Array<string>>(
		newServiceData?.images.map((image) => image.mime_type) || [],
	);

	const [selectedFilePreview, setSelectedFilePreview] = React.useState<Array<string>>(
		newServiceData?.images.map((image) => image.caption) || [],
	);

	const [category, setCategory] = React.useState<Array<string>>(
		newServiceData?.images.map((image) => image.category) || [],
	);

	const [openSnackbar, setOpenSnackbar] = React.useState(false);

	const [fileErrorSnackbarText, setFileErrorSnackbarText] = React.useState<string>("File size or type not supported");

	const [load, setLoad] = React.useState<boolean>(false);

	const [isFileDialogOpen, setFileDialogOpen] = React.useState(false);

	const [priority, setPriority] = React.useState<number>(0);

	const [step, setStep] = React.useState<number>(0);

	const router = React.useRef(useRouter()).current;

	const localLang = {
		sizeOrTypeNotSupported:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ಫೈಲ್ ಗಾತ್ರ ಅಥವಾ ಪ್ರಕಾರ ಬೆಂಬಲಿಸಲ್ಪಟ್ಟಿಲ್ಲ "
					: "File size or type not supported",
		isAn: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಒಂದು" : " is an",
		are: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಇವು" : " are",
		unsupported: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಅಸಮರ್ಥವಾಗಿದೆ" : " unsupported",
		file: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಫೈಲ್" : " file",
		files: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಫೈಲ್ಗಳು" : " files",
		and: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಮತ್ತು" : " and",
		has: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಹೊಂದಿದೆ" : " has",
		have: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? " ಹೊಂದಿದುವು" : " have",
		beenDeselected:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? " ಸ್ವಯಂಚಾಲಿತವಾಗಿ ರದ್ದುಗೊಂಡಿದೆ."
					: " been deselected automatically.",
		jpegOrPng: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "JPEG ಅಥವಾ PNG" : "JPEG or PNG",
		imgMaxSize:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಚಿತ್ರ ಗಾತ್ರ ಗರಿಷ್ಠ 10MB" : "Max upload 10MB",
		category: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ವರ್ಗ" : "Category",
		exterior: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಬಾಹ್ಯ" : "Exterior",
		interior: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಆಂತರಿಕ" : "Interior",
		masterPlan: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಮಾಸ್ಟರ್ ಪ್ಲಾನ್" : "Master plan",
		amenities: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಸೌಲಭ್ಯಗಳು" : "Amenities",
		routMap: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ರೂಟ್ ಮ್ಯಾಪ್" : "Route map",
		addMore: router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಹೆಚ್ಚು ಸೇರಿಸಿ" : "Add More",
		addPhotosFromYourGallary:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ನಿಮ್ಮ ಗ್ಯಾಲರಿ ನಿಂದ ಫೋಟೋಗಳನ್ನು ಸೇರಿಸಿ"
					: "Add photos from your gallery",
		maxLimit:
			router.locale === "ar-AE"
				? "ملف JPG أو PNG، بحجم لا يزيد عن 10 ميجابايت."
				: router.locale === "kn-IN"
					? "JPG ಅಥವಾ PNG ಫೈಲ್, ಗಾತ್ರವು 10MB ಗಿಂತ ಕಡಿಮೆ ಇರಬೇಕು."
					: "PG or PNG, file size no more than 10MB",
		moreThePixelMoreTheReach:
			router.locale === "ar-AE"
				? ""
				: router.locale === "kn-IN"
					? "ಹೆಚ್ಚು ಪಿಕ್ಸೆಲ್ ಹೆಚ್ಚು ತಲುಪುತ್ತದೆ"
					: "More the pixels more the reach",
		startUploading:
			router.locale === "ar-AE" ? "" : router.locale === "kn-IN" ? "ಅಪ್ಲೋಡ್ ಪ್ರಾರಂಭಿಸಿ" : "Start Uploading",
		coverImageText:
			router.locale === "ar-AE"
				? "اضغط على الصورة المحددة لجعلها صورة الغلاف"
				: router.locale === "kn-IN"
					? "ಆಯ್ದ ಚಿತ್ರದ ಮೇಲೆ ಟ್ಯಾಪ್ ಮಾಡಿ, ಅದನ್ನು ಕವರ್ ಚಿತ್ರವನ್ನಾಗಿ ಮಾಡಿರಿ"
					: "Tap on selected image to make it cover image",
	};

	interface UploadProgress {
		fileId: ObjectId | null;
		percentage: number;
		stage: string;
	}

	const handleUpload = async (validFiles: Array<FileAndMore>, filePreview: Array<string>) => {
		setLoad(true);
		dispatch(setServiceIsImagesUploadingThunk(true));
		await uploadFiles(
			session ? (session.authorized ? (session.session_id ? session.session_id : "") : "") : "",
			validFiles.map((file: FileAndMore) => ({
				file: file.file,
				mime_type: file.mime_type,
				original_file_id: new ObjectId(),
				original_file_server_path: "",
				original: true,
				is_business_profile: isBusinessProfile || isNewBusinessProfile ? true : false,
				business_profile_id: businessProfiles ? new ObjectId(businessProfiles.page_id) : new ObjectId(),
				entity: "service_regular",
				entity_id: new ObjectId(),
				use_case: "service_image",
				uiPath: "/",
				isPublic: false,
				text: businessProfiles ? businessProfiles.page_title : "",
			})),
			(uploadProgress: Array<UploadProgress>) => {
				const data = uploadProgress.map((progress) => progress.fileId);

				const uploadedProg = uploadProgress.map((progress) => progress);

				const stageInfo = uploadProgress.map((progress) => progress.stage);

				fileId[0] !== null ? setFileId([...fileId, ...data]) : setFileId(data);

				setMimeType([...mimeType, ...validFiles.map((file: FileAndMore) => file.mime_type)]);

				setCategory([...category, ...validFiles.map((file: FileAndMore) => "exterior")]);

				const newImageArray = [
					...(newServiceData?.images.map((image) => ({
						file_id: image.file_id,
						mime_type: image.mime_type,
						category: image.category,
						priority: image.priority,
						caption: image.caption,
						stage: image.stage,
					})) || []),
					...data.map((file_id: ObjectId, index: number) => ({
						file_id: file_id,
						mime_type: validFiles[index]?.mime_type || "", // Ensure mime_type is a string
						category: "exterior",
						priority: 1,
						caption: filePreview[index],
						stage: stageInfo[index],
					})),
				].filter(
					(image, index, self) => index === self.findIndex((t) => t.file_id?.toString() === image.file_id?.toString()),
				);

				const allFetched = uploadedProg.every((progress) => progress.stage === "FETCHED_FILE_INFORMATION");

				if (allFetched) {
					stage[0] !== "NOT_STARTED" ? setStage([...stage, ...stageInfo]) : setStage(stageInfo);
					dispatch(
						setCreateNewServiceUploadImagesThunk(
							newImageArray.map((image, index) => ({
								file_id: image.file_id,
								mime_type: image.mime_type,
								category: image.category,
								priority: index,
								caption: filePreview[index],
								stage: image.stage,
							})),
						),
					);
				}
			},
		);
		dispatch(setServiceIsImagesUploadingThunk(false));
		dispatch(setServiceImagesUploadCompletedThunk(true));
		setLoad(false);
	};

	// lets create a function to handle the priority of the images

	const handleUpdatePriority = (indexValue: number) => {
		setPriority(indexValue);
		let p = 1;
		dispatch(
			setCreateNewServiceUploadImagesThunk(
				newServiceData?.images.map((image, index) => ({
					file_id: image.file_id,
					mime_type: image.mime_type,
					category: image.category,
					priority: indexValue === index ? 0 : p++,
					caption: image.caption,
					stage: image.stage,
				})),
			),
		);
	};

	const handleRemoveImage = async (index: number) => {
		const newSelectedFilePreview = [...selectedFilePreview];
		const newSelectedFiles = [...selectedFiles];
		const newFileId = [...fileId];
		const newStage = [...stage];
		const newMimeType = [...mimeType];
		const newCategory = [...category];

		newSelectedFilePreview.splice(index, 1);
		newSelectedFiles.splice(index, 1);
		newFileId.splice(index, 1);
		newStage.splice(index, 1);
		newMimeType.splice(index, 1);
		newCategory.splice(index, 1);

		// Update the state with the new arrays
		setSelectedFilePreview(newSelectedFilePreview);
		setSelectedFiles(newSelectedFiles);
		setFileId(newFileId);
		setStage(newStage);
		setMimeType(newMimeType);
		setCategory(newCategory);
		setPriority(priority);
		dispatch(
			setCreateNewServiceUploadImagesThunk(
				newFileId.map((file_id: ObjectId, index: number) => ({
					file_id: file_id,
					mime_type: newMimeType[index],
					category: newCategory[index],
					priority: priority === index ? 0 : 1,
					caption: newSelectedFilePreview[index],
					stage: newStage[index],
				})),
			),
		);
	};

	React.useEffect(() => {
		if (isFileDialogOpen && input.current) {
			input.current.value = "";
			input.current.click();
			// Set isFileDialogOpen to false after opening the dialog
			setFileDialogOpen(false);
		}
	}, [isFileDialogOpen]);

	return (
		<React.Fragment>
			<Snack
				open={openSnackbar}
				autoHideDuration={3000}
				onClose={() => setOpenSnackbar(false)}
			>
				<Alert
					onClose={() => setOpenSnackbar(false)}
					severity="error"
					sx={{ width: "100%" }}
				>
					{fileErrorSnackbarText}
				</Alert>
			</Snack>
			{/* Upload Image Modal */}

			<CustomDialog>
				<CustomDialogContent>
					<InputContent
						ref={input}
						type="file"
						accept="image/jpeg, image/png"
						multiple={true}
						onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
							const files = event.target.files;
							const allowedTypes = ["image/jpeg", "image/png"];
							const allowedExtensions = ["jpg", "jpeg", "png"];
							const uploadType = "property_or_services";
							if (!files?.length) return setOpenSnackbar(true);

							const { validFiles, invalidFiles } = await validateFiles(
								Array.from(files),
								allowedTypes,
								allowedExtensions,
								uploadType,
							);

							if (validFiles.length) {
								const previews = validFiles.map((f) => URL.createObjectURL(f.file));
								setSelectedFiles((prev) => [...prev, ...validFiles]);
								handleUpload(validFiles, [...selectedFilePreview, ...previews]);
								setSelectedFilePreview((prev) => [...prev, ...previews]);
								setStep(0);
							}

							if (invalidFiles.length) {
								const names = invalidFiles.map((f) => f.name).join(", ");
								const display = names.length > 15 ? names.slice(0, 15) + "..." : names;
								setFileErrorSnackbarText(
									`${display} ${invalidFiles.length === 1 ? localLang.isAn : localLang.are}` +
										`${localLang.unsupported}${invalidFiles.length === 1 ? localLang.file : localLang.files}` +
										`${localLang.and}${invalidFiles.length === 1 ? localLang.has : localLang.have}${localLang.beenDeselected}`,
								);
								setOpenSnackbar(true);
							}
						}}
					/>
					{selectedFilePreview.length === 0 && step === 0 && (
						<div style={{ display: "flex", flexDirection: "column", gap: "1rem", width: "100%" }}>
							<UploadContent onClick={() => (isNewBusinessProfile ? setStep(1) : input.current?.click())}>
								<SvgIcon
									component={RequestImagesIcon}
									viewBox="0 0 41 33"
									width={24}
									height={24}
									sx={{ width: "2.563rem", height: "1.976rem", fill: "none" }}
								/>
								<Typography sx={{ color: theme.palette.text.primary, fontSize: "0.875rem" }}>
									{localLang.addPhotosFromYourGallary}
								</Typography>
								<Typography sx={{ color: theme.palette.text.secondary, fontSize: "0.75rem" }}>
									{localLang.maxLimit}
								</Typography>
							</UploadContent>

							{/* <DividerContainer>
								<Divider style={{ flex: 1 }} />
								<span style={{ padding: "0 8px", fontSize: "1rem", color: "#333" }}>
									{router.locale === "ar-AE" ? "أو" : "OR"}
								</span>
								<Divider style={{ flex: 1 }} />
							</DividerContainer>

							<UploadContent sx={{ flexDirection: "row", gap: "1rem" }}>
								<PhotoCameraOutlinedIcon />
								<Typography sx={{ color: "#9E9E9E", fontSize: "0.875rem" }}>
									{localLang.addPhotosFromYourGallary}
								</Typography>
							</UploadContent> */}
						</div>
					)}

					{step === 0 && (
						<ImagePreviewContainer>
							{selectedFilePreview.length > 0 && allFilesFetched && (
								<div
									style={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "space-between",
										alignItems: "center",
										width: "100%",
									}}
								>
									<Typography
										sx={{
											color: theme.palette.text.primary,
											fontSize: "1rem",
											fontWeight: 700,
										}}
									>
										Uploaded {selectedFilePreview.length} {selectedFilePreview.length > 1 ? "images" : "image"}
									</Typography>
									{/* <IconButton
										onClick={() => {
											input.current?.click();
										}}
									>
										<AddOutlinedIcon />
									</IconButton>
									 */}
									<BeegruButton
										variant="contained"
										sx={{
											color: theme.palette.primary.main,
											fontSize: "0.875rem",
											fontWeight: 500,
											textTransform: "none",
										}}
										onClick={() => {
											input.current?.click();
										}}
									>
										<AddOutlinedIcon /> Add more
									</BeegruButton>
								</div>
							)}
							{selectedFilePreview.length > 0 && allFilesFetched && (
								<div style={{ display: "flex", position: "relative", width: "100%" }}>
									<Chip
										label="Cover photo"
										sx={{
											position: "absolute",
											top: "0.5rem",
											left: "0.5rem",
											backgroundColor: "#FFF",
											color: "#7E7D7D",
											fontSize: "0.75rem",
											fontWeight: 400,
											borderRadius: "0.75rem",
											boxShadow: "0px 4px 18px rgba(0,0,0,0.05)",
											height: "20px",
											zIndex: 1,
										}}
									/>
									<PreviewImage
										src={selectedFilePreview[priority]}
										alt="Property Images"
										referrerPolicy="no-referrer"
										style={{ width: "100%", height: "10.5rem", borderRadius: "16px" }}
									/>
								</div>
							)}

							{selectedFilePreview.map((file, index) => (
								<ContentContainer key={index}>
									{file && (
										<ImageContainer onClick={() => handleUpdatePriority(index)}>
											<PreviewImage
												src={file}
												alt="Property Images"
												referrerPolicy="no-referrer"
											/>
											<ProgressContainer>
												{(stage[index] && stage[index] !== "FETCHED_FILE_INFORMATION") ||
													(stage[index] === undefined && <CircularProgress sx={{ color: "#FFF" }} />)}
											</ProgressContainer>
											{stage[index] === "FETCHED_FILE_INFORMATION" && (
												<IconButton
													size="small"
													onClick={() => handleRemoveImage(index)}
													sx={{
														position: "absolute",
														top: "0.3rem",
														right: "0.5rem",
														background: "#FFFFFF90",
														padding: "0.5rem",
														height: "1.5rem",
														width: "1.5rem",
														zIndex: 1,
													}}
												>
													<DeleteOutline sx={{ color: "#E31B0C", fontSize: "1.25rem" }} />
												</IconButton>
											)}
										</ImageContainer>
									)}
								</ContentContainer>
							))}
						</ImagePreviewContainer>
					)}

					{step === 1 && (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
								gap: "1rem",
								width: "100%",

								animation: "fadeIn 0.5s ease-in-out",
							}}
						>
							<img
								src={displayImage}
								alt="Display Image"
								style={{ width: "100%", height: "auto" }}
							/>
							<Typography sx={{ color: theme.palette.text.primary, fontSize: "1.5rem", fontWeight: 700 }}>
								{localLang.moreThePixelMoreTheReach}
							</Typography>
							<div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", width: "100%" }}>
								<Typography
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "flex-start",
										alignItems: "center",
										gap: "1rem",
										fontSize: "0.75rem",
										fontWeight: 400,
									}}
								>
									<DoneOutlinedIcon sx={{ color: theme.palette.primary.main }} />
									{router.locale === "ar-AE"
										? "قم بتحميل صور واضحة فقط، فهذا سيساعدك على زيادة التحويلات لعقارك."
										: router.locale === "kn-IN"
											? "ದಯವಿಟ್ಟು ಸ್ಪಷ್ಟವಾದ ಚಿತ್ರಗಳನ್ನು ಮಾತ್ರ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ, ಇದರಿಂದ ನಿಮ್ಮ ಆಸ್ತಿಯ ಪರಿವರ್ತನೆಗಳನ್ನು ಹೆಚ್ಚಿಸಲು ಸಹಾಯವಾಗುತ್ತದೆ."
											: "Upload only clear images this will help you increase conversions for your property"}
								</Typography>

								<Typography
									sx={{
										display: "flex",
										flexDirection: "row",
										justifyContent: "flex-start",
										alignItems: "center",
										gap: "1rem",
										fontSize: "0.75rem",
										fontWeight: 400,
									}}
								>
									<DoneOutlinedIcon sx={{ color: theme.palette.primary.main }} />
									{router.locale === "ar-AE"
										? "يجب ألا تحتوي الصور على محتوى ضبابي، لأنه قد يسبب مشكلات للمستخدمين في الفهم."
										: router.locale === "kn-IN"
											? "ಚಿತ್ರಗಳಲ್ಲಿ ಮುಸುಕು ಕಂಡಿರುವ ವಿಷಯಗಳಿರಬಾರದು, ಇದು ಬಳಕೆದಾರರು ಅರ್ಥಮಾಡಿಕೊಳ್ಳಲು ತೊಂದರೆ ಉಂಟುಮಾಡಬಹುದು."
											: "Images should not contain blurry content, as it can create problems for users to understand."}
								</Typography>
								<BeegruButton
									variant="contained"
									sx={{
										width: "100%",
										height: "3rem",
										backgroundColor: theme.palette.primary.main,
										color: theme.palette.common.white,
										"&:hover": { backgroundColor: theme.palette.primary.dark },
									}}
									onClick={() => input.current?.click()}
								>
									{localLang.startUploading}
								</BeegruButton>
							</div>
						</div>
					)}

					{selectedFilePreview.length > 0 && allFilesFetched && (
						<div
							style={{
								position: "absolute",
								bottom: "3.5rem",
								left: "1rem",
								right: "1rem",
								width: "calc(100% - 2rem)",
								display: "flex",
								alignItems: "center",
								gap: "0.5rem",
								padding: "0.5rem 1rem",
								backgroundColor: "#FFC800",
								borderRadius: "8px",
								boxSizing: "border-box",
							}}
						>
							<InfoOutlinedIcon />
							<Typography
								variant="caption"
								sx={{ color: "#666", fontSize: "0.75rem", fontWeight: 400 }}
							>
								{localLang.coverImageText}
							</Typography>
						</div>
					)}
				</CustomDialogContent>
			</CustomDialog>
		</React.Fragment>
	);
};

export default UploadImages;
